import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Tooltip,
} from '@ouster/webviz-react-components';

import DOMPurify from 'dompurify';

import { useState, useRef, useEffect, useCallback } from 'react';

import styles from './HtmlEula.module.css';

type HtmlEulaProps = {
  handleClose?: () => void;
  handleAcceptEula?: () => void;
  eulaPdfLink?: string;
  eulaLink: string; // The link to the EULA HTML file
  showAction?: boolean;
};

// Add some buffer to loosen the strict check for scrolling to bottom
const bufferPercentage = 0.5;

export default function HtmlEula({
  handleClose,
  handleAcceptEula,
  eulaPdfLink,
  eulaLink,
  showAction = false,
}: HtmlEulaProps) {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScrollToBottom, setIsScrollToBottom] = useState(false);
  const [geoRestrictionConsent, setIsGeoRestrictionConsent] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);

  // Fetch the HTML content from the EULA link
  useEffect(() => {
    if (!eulaLink) return;

    const loadEula = async () => {
      try {
        const response = await fetch(eulaLink);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const body = doc.body.innerHTML;

        // SA3-XSS-01: DOMParser is a parse round-trip, not a sanitizer. Sanitize
        // the remote EULA HTML before it is rendered via dangerouslySetInnerHTML.
        setHtmlContent(DOMPurify.sanitize(body));
      } catch (error) {
        console.error('Failed to load EULA:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEula();
  }, [eulaLink]);

  // Scroll handling to check if user has scrolled to the bottom
  const handleScroll = useCallback(() => {
    if (viewerRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = viewerRef.current;

      const bufferHeight = clientHeight * bufferPercentage;
      const isCloseToBottom =
        scrollHeight - scrollTop <= clientHeight + bufferHeight;

      setIsScrollToBottom(isCloseToBottom);
    }
  }, []);

  const handleDownloadEula = () => {
    if (!eulaPdfLink) return;

    const link = document.createElement('a');
    link.href = eulaPdfLink;
    link.setAttribute('download', 'EULA.pdf');
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const prohibitedCountries = [
    'Cuba',
    'Iran',
    'North Korea',
    'Russia',
    'Belarus',
    'Syria',
    'Russian-occupied territories of Ukraine',
  ];

  const checkboxLabel = () => (
    <span className={styles['checkbox-label']}>
      I confirm that I am not uploading data from{' '}
      <Tooltip
        label={prohibitedCountries.join(', ')}
        aria-label="List of prohibited countries"
      >
        <span className={styles['checkbox-label-info']}>
          prohibited areas or countries
        </span>
      </Tooltip>
      , and any sensitive, classified, or otherwise restricted data
    </span>
  );

  return (
    <Box className={styles.container} data-testid="html-eula-root">
      {isLoading ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH="50vh"
          data-testid="html-eula-loading"
        >
          <CircularProgress isIndeterminate size="50px" thickness={10} />
        </Box>
      ) : (
        <Box
          className={styles['eula-container']}
          minW={showAction ? undefined : '800px'}
          ref={viewerRef}
          onScroll={handleScroll}
          data-testid="html-eula-content"
        >
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: htmlContent || '' }}
            style={{ whiteSpace: 'pre-wrap' }} // Helps with formatting the HTML
          />
        </Box>
      )}

      {showAction && (
        <Box
          className={styles['bottom-console']}
          display="flex"
          flexDirection="column"
          columnGap="0.5rem"
          alignItems="flex-start"
          rowGap="1rem"
        >
          <Box
            display="flex"
            columnGap={1}
            alignSelf="flex-start"
            alignItems="center"
          >
            <Checkbox
              label=""
              isChecked={geoRestrictionConsent}
              onChange={(e) => {
                setIsGeoRestrictionConsent(e.target.checked);
              }}
              data-testid="html-eula-checkbox"
            />
            {checkboxLabel()}
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            columnGap="0.5rem"
            alignItems="center"
            alignSelf="flex-end"
          >
            {eulaPdfLink && (
              <Button
                colorScheme="gray"
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                onClick={handleDownloadEula}
                data-testid="html-eula-download-btn"
              >
                Download
              </Button>
            )}

            <Box display="flex" columnGap={2}>
              <Button
                colorScheme="gray"
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                onClick={() => handleClose && handleClose()}
                data-testid="html-eula-reject-btn"
              >
                Reject
              </Button>
              <Button
                variant="solid"
                size={{ base: 'sm', md: 'md' }}
                disabled={!(isScrollToBottom && geoRestrictionConsent)}
                onClick={() => handleAcceptEula && handleAcceptEula()}
                data-testid="html-eula-accept-btn"
              >
                Accept
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
